const NS = 'vorakit_fl_';

const ls = {
  get: (key, fallback = null) => {
    try {
      const v = localStorage.getItem(NS + key);
      return v !== null ? JSON.parse(v) : fallback;
    } catch { return fallback; }
  },
  set: (key, value) => {
    try { localStorage.setItem(NS + key, JSON.stringify(value)); } catch { /* storage unavailable */ }
  },
};

const todayKey = () => new Date().toISOString().slice(0, 10);

// ─── Settings ────────────────────────────────────────────────────────────────

export const getSettings = (defaults) => ({ ...defaults, ...ls.get('settings', {}) });
export const saveSettings = (s) => ls.set('settings', s);

// ─── Stats ───────────────────────────────────────────────────────────────────

export const getTodayStats = () =>
  ls.get(`stats_${todayKey()}`, { sessions: 0, minutes: 0 });

export const recordSession = (minutes) => {
  const key = `stats_${todayKey()}`;
  const s = ls.get(key, { sessions: 0, minutes: 0 });
  ls.set(key, { sessions: s.sessions + 1, minutes: Math.round(s.minutes + minutes) });
  _updateStreak();
};

const _updateStreak = () => {
  const today = todayKey();
  const streak = ls.get('streak', { count: 0, lastDate: null });
  if (streak.lastDate === today) return;
  const yd = new Date();
  yd.setDate(yd.getDate() - 1);
  const yesterday = yd.toISOString().slice(0, 10);
  ls.set('streak', {
    count: streak.lastDate === yesterday ? streak.count + 1 : 1,
    lastDate: today,
  });
};

export const getStreak = () => ls.get('streak', { count: 0, lastDate: null });

// ─── Intention ───────────────────────────────────────────────────────────────

export const getIntention = () => {
  const stored = ls.get('intention', { text: '', date: null });
  return stored.date === todayKey() ? stored.text : '';
};

export const saveIntention = (text) =>
  ls.set('intention', { text, date: todayKey() });

// ─── Music ───────────────────────────────────────────────────────────────────

const MUSIC_DEFAULTS = {
  source: 'none',
  spotifyUrl: '',
  youtubeUrl: '',
  /** @type {'audio_first' | 'standard'} */
  youtubePresentation: 'audio_first',
  /** 0–100 */
  youtubeVolume: 80,
  youtubeMuted: false,
};

export const getMusicState = () => ({ ...MUSIC_DEFAULTS, ...ls.get('music', {}) });

export const saveMusicState = (s) => ls.set('music', s);

// ─── Last timer mode (when no running snapshot) ────────────────────────────

export const getLastTimerMode = () => ls.get('last_timer_mode', null);
export const saveLastTimerMode = (mode) => ls.set('last_timer_mode', mode);

// ─── Theme ───────────────────────────────────────────────────────────────────

export const getThemeId = () => ls.get('theme', 'midnight');
export const saveThemeId = (id) => ls.set('theme', id);

// ─── Unified music recents (Spotify + YouTube, single list) ──────────────────

const MUSIC_RECENTS_MAX = 8;
const MUSIC_RECENTS_KEY = 'music_recents';
const MUSIC_RECENTS_MIGRATED = 'music_recents_migrated_v2';

const _readLegacyUrlStrings = (key) => {
  const h = ls.get(key, []);
  return Array.isArray(h) ? h.filter((x) => typeof x === 'string' && x.trim()) : [];
};

const _migrateLegacyRecentsIfNeeded = () => {
  if (ls.get(MUSIC_RECENTS_MIGRATED, false)) return;

  const current = ls.get(MUSIC_RECENTS_KEY, []);
  if (Array.isArray(current) && current.length > 0 && typeof current[0] === 'object' && current[0].url && current[0].source) {
    ls.set(MUSIC_RECENTS_MIGRATED, true);
    return;
  }

  const out = [];
  let ts = Date.now();
  _readLegacyUrlStrings('music_url_history_spotify').forEach((url) => {
    out.push({ url: url.trim(), source: 'spotify', ts: ts-- });
  });
  _readLegacyUrlStrings('music_url_history_youtube').forEach((url) => {
    out.push({ url: url.trim(), source: 'youtube', ts: ts--, youtubePresentation: 'audio_first' });
  });

  const byUrl = new Map();
  out.sort((a, b) => b.ts - a.ts).forEach((e) => {
    if (!byUrl.has(e.url)) byUrl.set(e.url, e);
  });

  const merged = [...byUrl.values()].slice(0, MUSIC_RECENTS_MAX);
  ls.set(MUSIC_RECENTS_KEY, merged);
  ls.set(MUSIC_RECENTS_MIGRATED, true);
};

/**
 * @returns {Array<{ url: string, source: 'spotify'|'youtube', ts: number, youtubePresentation?: string, spotifyTitle?: string, youtubeTitle?: string }>}
 */
export const getMusicRecents = () => {
  _migrateLegacyRecentsIfNeeded();
  const raw = ls.get(MUSIC_RECENTS_KEY, []);
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (x) =>
        x &&
        typeof x === 'object' &&
        typeof x.url === 'string' &&
        x.url.trim() &&
        (x.source === 'spotify' || x.source === 'youtube'),
    )
    .map((x) => ({
      url: x.url.trim(),
      source: x.source,
      ts: typeof x.ts === 'number' && Number.isFinite(x.ts) ? x.ts : Date.now(),
      youtubePresentation:
        x.source === 'youtube'
          ? (x.youtubePresentation === 'standard' ? 'standard' : 'audio_first')
          : undefined,
      ...(x.source === 'spotify' &&
      typeof x.spotifyTitle === 'string' &&
      x.spotifyTitle.trim()
        ? { spotifyTitle: x.spotifyTitle.trim().slice(0, 200) }
        : {}),
      ...(x.source === 'youtube' &&
      typeof x.youtubeTitle === 'string' &&
      x.youtubeTitle.trim()
        ? { youtubeTitle: x.youtubeTitle.trim().slice(0, 200) }
        : {}),
    }))
    .sort((a, b) => b.ts - a.ts)
    .slice(0, MUSIC_RECENTS_MAX);
};

/**
 * @param {{ url: string, source: 'spotify'|'youtube', youtubePresentation?: 'audio_first'|'standard', spotifyTitle?: string, youtubeTitle?: string }} entry
 */
export const addMusicRecent = ({ url, source, youtubePresentation, spotifyTitle, youtubeTitle }) => {
  _migrateLegacyRecentsIfNeeded();
  const t = url?.trim();
  if (!t || (source !== 'spotify' && source !== 'youtube')) return;

  const all = getMusicRecents();
  const prevEntry = all.find((e) => e.url === t);
  const prev = all.filter((e) => e.url !== t);
  const resolvedSpotifyTitle =
    source === 'spotify'
      ? (typeof spotifyTitle === 'string' && spotifyTitle.trim()
          ? spotifyTitle.trim().slice(0, 200)
          : prevEntry?.spotifyTitle)
      : undefined;
  const resolvedYoutubeTitle =
    source === 'youtube'
      ? (typeof youtubeTitle === 'string' && youtubeTitle.trim()
          ? youtubeTitle.trim().slice(0, 200)
          : prevEntry?.youtubeTitle)
      : undefined;
  const entry = {
    url: t,
    source,
    ts: Date.now(),
    ...(resolvedSpotifyTitle ? { spotifyTitle: resolvedSpotifyTitle } : {}),
    ...(resolvedYoutubeTitle ? { youtubeTitle: resolvedYoutubeTitle } : {}),
    ...(source === 'youtube'
      ? { youtubePresentation: youtubePresentation === 'standard' ? 'standard' : 'audio_first' }
      : {}),
  };
  const next = [entry, ...prev].slice(0, MUSIC_RECENTS_MAX);
  ls.set(MUSIC_RECENTS_KEY, next);
};

/** Merge fields onto the raw recent row matching `url` (e.g. after async oEmbed title fetch). */
export const patchMusicRecentByUrl = (url, patch) => {
  const t = url?.trim();
  if (!t || !patch || typeof patch !== 'object') return;
  _migrateLegacyRecentsIfNeeded();
  const raw = ls.get(MUSIC_RECENTS_KEY, []);
  if (!Array.isArray(raw)) return;
  const next = raw.map((e) => {
    if (!e || typeof e.url !== 'string' || e.url.trim() !== t) return e;
    return { ...e, ...patch };
  });
  ls.set(MUSIC_RECENTS_KEY, next);
};

export const clearMusicRecents = () => {
  ls.set(MUSIC_RECENTS_KEY, []);
};

/** Remove only Spotify or only YouTube entries from unified recents. */
export const clearMusicRecentsBySource = (source) => {
  if (source !== 'spotify' && source !== 'youtube') return;
  _migrateLegacyRecentsIfNeeded();
  const next = getMusicRecents().filter((e) => e.source !== source);
  ls.set(MUSIC_RECENTS_KEY, next);
};

/** Remove one recent by URL (dedupe-safe). */
export const removeMusicRecent = (url) => {
  const t = url?.trim();
  if (!t) return;
  _migrateLegacyRecentsIfNeeded();
  const next = getMusicRecents().filter((e) => e.url !== t);
  ls.set(MUSIC_RECENTS_KEY, next);
};

